import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GitBranch, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_COLORS = {
  pending: 'bg-[#fbdd68]/10 text-[#fbdd68] border-[#fbdd68]/30',
  approved: 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30',
  declined: 'bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/30',
  cancelled: 'bg-[#8892b0]/10 text-[#8892b0] border-[#8892b0]/30',
};

export const TransferRequests = () => {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);     // full row + impact for the confirm dialog
  const [mode, setMode] = useState(null);         // 'approve' | 'decline'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    try { const { data } = await ax.get(`${API}/admin/transfers?status=${status}&limit=50`); setRows(data.transfers); }
    catch { setRows([]); }
  }, [status]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openDecision = async (row, m) => {
    setMode(m); setReason('');
    try { const { data } = await ax.get(`${API}/admin/transfers/${row.id}`); setDetail(data); }
    catch { setDetail(row); }
  };

  const decide = async () => {
    setBusy(true);
    try {
      if (mode === 'approve') {
        await ax.post(`${API}/admin/transfers/${detail.id}/approve`, { confirm: true });
        toast.success('Transfer approved');
      } else {
        await ax.post(`${API}/admin/transfers/${detail.id}/decline`, { reason });
        toast.success('Request declined');
      }
      setDetail(null); setMode(null); fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete that');
    } finally { setBusy(false); }
  };

  return (
    <Card className="glass-panel rounded-2xl border-0" data-testid="transfer-requests-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#fbdd68]" /> Transfer requests
          </CardTitle>
          <div className="flex gap-1">
            {['pending', 'approved', 'declined', 'cancelled'].map((s) => (
              <button key={s} data-testid={`transfers-filter-${s}`} onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${status === s ? 'bg-[#fbdd68]/10 text-[#fbdd68]' : 'text-[#8892b0] hover:text-white'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`transfer-row-${r.id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">{r.person?.name || r.person?.email}</p>
                <p className="text-xs text-[#8892b0] truncate">
                  {r.person?.email} — leaving <span className="text-white">{r.from?.org_name}</span> ({r.from?.role}) · license {r.to?.license_masked}
                </p>
                <p className="text-[10px] text-[#8892b0]/60 mt-0.5">
                  requested {r.requested_at ? new Date(r.requested_at).toLocaleString() : ''}
                  {r.decided_at ? ` · decided ${new Date(r.decided_at).toLocaleString()} by ${r.decided_by_email || ''}` : ''}
                  {r.reason ? ` · reason: ${r.reason}` : ''}
                </p>
              </div>
              <Badge className={STATUS_COLORS[r.status] || STATUS_COLORS.cancelled}>{r.status}</Badge>
              {r.status === 'pending' && (
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`transfer-approve-${r.id}`} onClick={() => openDecision(r, 'approve')}
                    className="bg-[#22c55e]/15 text-[#22c55e] hover:bg-[#22c55e]/25 border border-[#22c55e]/30">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" data-testid={`transfer-decline-${r.id}`} onClick={() => openDecision(r, 'decline')}
                    className="bg-[#ef4444]/15 text-[#ef4444] hover:bg-[#ef4444]/25 border border-[#ef4444]/30">
                    <X className="w-4 h-4 mr-1" /> Decline
                  </Button>
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && <p className="text-center text-[#8892b0] py-8" data-testid="transfers-empty">No {status} requests</p>}
        </div>
      </CardContent>

      <Dialog open={!!detail && !!mode} onOpenChange={(o) => { if (!o) { setDetail(null); setMode(null); } }}>
        <DialogContent data-testid="transfer-decision-dialog" className="bg-[#0a1240] border border-[rgba(251,221,104,0.2)] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#fbdd68]">
              {mode === 'approve' ? 'Approve this transfer?' : 'Decline this request'}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p>
                <span className="font-semibold">{detail.person?.name || detail.person?.email}</span> leaves{' '}
                <span className="font-semibold text-[#fbdd68]">{detail.from?.org_name}</span> and becomes the owner of
                their own organization (license {detail.to?.license_masked}).
              </p>
              {detail.impact && (
                <div className="p-3 rounded-lg bg-[rgba(20,27,80,0.6)] border border-[rgba(251,221,104,0.1)] text-xs text-[#c8d0e8] space-y-1">
                  <p>Employee record kept (inactive) in the old org: {detail.impact.employee_rows}</p>
                  <p>Upcoming claimed events staying in the old org: {detail.impact.upcoming_claimed_events}</p>
                  <p>Programs that stay with the old org: {detail.impact.programs_linked_to_from_org}</p>
                </div>
              )}
              {mode === 'approve' && (
                <p className="text-xs text-[#8892b0]">
                  The old org keeps their history; their sign-in moves to the new org and old sessions end.
                  The old org's owner is told by email and a dashboard notice.
                </p>
              )}
              {mode === 'decline' && (
                <textarea data-testid="transfer-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                  rows={3} placeholder="Reason the person will read (required)"
                  className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none bg-[#000e2a]/80 border border-[rgba(251,221,104,0.2)]" />
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="transfer-decision-confirm" onClick={decide}
              disabled={busy || (mode === 'decline' && reason.trim().length < 3)}
              className={mode === 'approve' ? 'bg-[#22c55e] text-[#04120a] font-bold hover:bg-[#3ddf75]' : 'bg-[#ef4444] text-white font-bold hover:bg-[#f87171]'}>
              {busy ? 'Working…' : mode === 'approve' ? 'Approve transfer' : 'Decline request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
